import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "lafleur-cart-v2";

const makeLineKey = (item) => `${item.product_id}::${item.variant_id || ""}::${item.notes || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);

  /**
   * Add a product to the cart.
   * @param {Object} product - base product { id, name, image }
   * @param {Object} opts    - { qty, variant, notes }
   *   variant = { id, label, price } — for configurable products
   *   notes   = string with customization details
   */
  const add = (product, opts = {}) => {
    const { qty = 1, variant = null, notes = null } = opts;
    const price = variant ? variant.price : product.price;
    const name = variant ? `${product.name.replace(/ · .*/, "")} · ${variant.label}` : product.name;
    const line = {
      product_id: product.id,
      variant_id: variant ? variant.id : null,
      variant_label: variant ? variant.label : null,
      name,
      price,
      image: product.image,
      notes,
      quantity: qty,
    };
    setItems((prev) => {
      const key = makeLineKey(line);
      const existing = prev.find((p) => makeLineKey(p) === key);
      if (existing) {
        return prev.map((p) => (makeLineKey(p) === key ? { ...p, quantity: p.quantity + qty } : p));
      }
      return [...prev, line];
    });
    setIsOpen(true);
  };

  const updateQty = (lineKey, qty) => {
    setItems((prev) =>
      prev.map((p) => (makeLineKey(p) === lineKey ? { ...p, quantity: qty } : p)).filter((p) => p.quantity > 0)
    );
  };

  const remove = (lineKey) => setItems((prev) => prev.filter((p) => makeLineKey(p) !== lineKey));
  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = { items, add, updateQty, remove, clear, subtotal, count, isOpen, setIsOpen, makeLineKey };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export const fmtPrice = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
